import { GuestWithRSVP } from '../services/api';

/**
 * Calculates attendance statistics for welcome party and wedding
 */
export const calculateAttendanceStats = (rsvps: GuestWithRSVP[]) => {
  const welcomePartyStats = {
    attending: 0,
    notAttending: 0
  };

  const weddingStats = {
    attending: 0,
    notAttending: 0
  };

  rsvps.forEach(guest => {
    if (guest.rsvp?.welcomeParty?.attending) {
      welcomePartyStats.attending += 1;
    } else {
      welcomePartyStats.notAttending += 1;
    }

    if (guest.rsvp?.wedding?.attending) {
      weddingStats.attending += 1;
    } else {
      weddingStats.notAttending += 1;
    }
  });

  return {
    welcomeParty: [
      { name: 'Attending', value: welcomePartyStats.attending, fill: '#4CAF50' },
      { name: 'Not Attending', value: welcomePartyStats.notAttending, fill: '#F44336' }
    ],
    wedding: [
      { name: 'Attending', value: weddingStats.attending, fill: '#4CAF50' },
      { name: 'Not Attending', value: weddingStats.notAttending, fill: '#F44336' }
    ]
  };
};

/**
 * Calculates transportation option statistics
 */
export const calculateTransportationStats = (rsvps: GuestWithRSVP[]) => {
  const transportationStats = {
    bus: 0,
    own: 0,
    notSpecified: 0
  };

  // Only count guests who are attending the wedding
  const weddingAttendees = rsvps.filter(guest => guest.rsvp?.wedding?.attending);

  weddingAttendees.forEach(guest => {
    const option = guest.rsvp?.transportation?.option;
    if (option === 'bus') {
      transportationStats.bus += 1;
    } else if (option === 'own') {
      transportationStats.own += 1;
    } else {
      transportationStats.notSpecified += 1;
    }
  });

  return [
    { name: 'Shuttle', value: transportationStats.bus, fill: '#4CAF50' },
    { name: 'Own Transportation', value: transportationStats.own, fill: '#FF9800' },
    { name: 'Not Specified', value: transportationStats.notSpecified, fill: '#9E9E9E' }
  ];
};

/**
 * Calculates relationship distribution statistics
 */
export const calculateRelationshipStats = (rsvps: GuestWithRSVP[]) => {
  const relationshipCounts: Record<string, number> = {};

  rsvps.forEach(guest => {
    const relationship = guest.relationship || 'Not Specified';
    relationshipCounts[relationship] = (relationshipCounts[relationship] || 0) + 1;
  });

  // Convert to array format for charts
  const colors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#795548', '#607D8B'];
  
  return Object.entries(relationshipCounts).map(([name, value], index) => ({
    name,
    value,
    fill: colors[index % colors.length]
  }));
};

/**
 * Calculates summary statistics
 */
export const calculateSummaryStats = (rsvps: GuestWithRSVP[]) => {
  const totalGuests = rsvps.length;
  const welcomePartyAttendees = rsvps.filter(guest => guest.rsvp?.welcomeParty?.attending).length;
  const weddingAttendees = rsvps.filter(guest => guest.rsvp?.wedding?.attending).length;
  const needingTransportation = rsvps.filter(guest => 
    guest.rsvp?.wedding?.attending && guest.rsvp?.transportation?.option === 'bus'
  ).length;

  return {
    totalGuests,
    welcomePartyAttendees,
    weddingAttendees,
    needingTransportation
  };
};

/**
 * Calculates RSVP status statistics
 */
export const calculateRsvpStatusStats = (rsvps: GuestWithRSVP[]) => {
  const responded = rsvps.filter(guest => guest.rsvpStatus === 'responded').length;
  const pending = rsvps.filter(guest => guest.rsvpStatus === 'pending').length;

  return [
    { name: 'Responded', value: responded, fill: '#4CAF50' },
    { name: 'Pending', value: pending, fill: '#FF9800' }
  ];
};

/**
 * Creates filter options for quick filtering
 */
export const getQuickFilterOptions = () => [
  { label: 'All Guests', value: 'all' },
  { label: 'Wedding Attendees', value: 'wedding' },
  { label: 'Welcome Party Attendees', value: 'welcomeParty' },
  { label: 'Needing Transportation', value: 'transportation' },
  { label: 'Pending RSVPs', value: 'pending' }
];

/**
 * Applies quick filter to the data
 */
export const applyQuickFilter = (rsvps: GuestWithRSVP[], filterValue: string) => {
  switch (filterValue) {
    case 'wedding':
      return rsvps.filter(guest => guest.rsvp?.wedding?.attending);
    case 'welcomeParty':
      return rsvps.filter(guest => guest.rsvp?.welcomeParty?.attending);
    case 'transportation':
      return rsvps.filter(guest => 
        guest.rsvp?.wedding?.attending && guest.rsvp?.transportation?.option === 'bus'
      );
    case 'pending':
      return rsvps.filter(guest => guest.rsvpStatus === 'pending');
    case 'all':
    default:
      return rsvps;
  }
};
